// ─── Recover from a deploy that replaced the code this tab was loaded with ────
//
// Pages are code-split, and each deploy publishes new hashed chunk files. A tab
// opened before a deploy still points at the old file names; when it first
// visits a lazily loaded page (e.g. /login after an idle sign-out) the request
// gets the SPA's index.html back instead of JavaScript, the dynamic import
// fails, and the error boundary showed "Something went wrong". Reloading picks
// up the current build, so do that automatically — once per short window, so a
// genuinely broken build can't loop.

const OC_RELOAD_KEY = "oc-stale-build-reload-at";
const OC_RELOAD_WINDOW_MS = 30_000;

/** A failed dynamic import / chunk load, as the major browsers word it. */
export function ocIsStaleBuildError(err: unknown): boolean {
  const msg = (err instanceof Error ? `${err.name} ${err.message}` : String(err ?? "")).toLowerCase();
  return (
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("importing a module script failed") ||
    msg.includes("unable to preload css") ||
    msg.includes("chunkloaderror") ||
    (msg.includes("mime type") && msg.includes("module"))
  );
}

/** Reload to the current build. Returns false if one was just tried (no loop). */
export function ocReloadForNewBuild(): boolean {
  try {
    const last = Number(sessionStorage.getItem(OC_RELOAD_KEY) || 0);
    if (Date.now() - last < OC_RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(OC_RELOAD_KEY, String(Date.now()));
  } catch {
    // Storage blocked: still reload once; the browser keeps no loop guard then.
  }
  window.location.reload();
  return true;
}

/** Vite reports a failed chunk preload with this event before the import throws. */
export function ocInstallStaleBuildRecovery(): void {
  window.addEventListener("vite:preloadError", (event) => {
    if (ocReloadForNewBuild()) event.preventDefault();
  });
}
